import React, { useState } from 'react';
import axios from 'axios';

/**
 * A masked contact value with a Reveal control.
 *
 * The server masks phone/name until the signed-in user reveals that specific
 * field on that specific record. A reveal is permanent - the server records it in
 * contact_reveals and every later page load comes back already unmasked - so this
 * component never has to remember anything across renders.
 *
 * Props:
 *   row         the record as returned by the API (carries _phone_masked etc.)
 *   field       'phone' | 'name'
 *   entityType  'client' | 'walkin' | 'broker'
 *   apiBase     API root
 */
const RevealContact = ({ row, field = 'phone', entityType, apiBase, style }) => {
  const [value, setValue] = useState(row?.[field]);
  const [masked, setMasked] = useState(
    field === 'phone' ? row?._phone_masked : row?._name_masked
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canReveal = field === 'phone' ? row?._can_reveal_phone : row?._can_reveal_name;

  if (!row) return <span>-</span>;

  // Nothing to gate: either the value is already in the clear (an exempt role, or
  // this user revealed it earlier) or there is no value at all.
  if (!masked) return <span style={style}>{value || '-'}</span>;

  const reveal = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(`${apiBase}/qr/reveal`, {
        entity_type: entityType,
        entity_id: row.id,
        field
      });
      setValue(res.data?.value);
      setMasked(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reveal');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px',
                   whiteSpace: 'nowrap', ...style }}>
      <span style={{ letterSpacing: '0.5px', color: '#94a3b8' }}>{value || '-'}</span>
      {canReveal && (
        <button
          type="button"
          onClick={reveal}
          disabled={busy}
          title="Reveal — this is recorded and cannot be undone"
          style={{
            border: '1px solid #cbd5e1', background: busy ? '#f1f5f9' : '#fff',
            borderRadius: '6px', padding: '2px 8px', fontSize: '11px',
            cursor: busy ? 'default' : 'pointer', color: '#0891b2', fontWeight: 600
          }}
        >
          {busy ? '…' : 'Reveal'}
        </button>
      )}
      {error && <span style={{ fontSize: '11px', color: '#ef4444' }}>{error}</span>}
    </span>
  );
};

export default RevealContact;
